import { useMemo } from 'react';
import { z } from 'zod';
import type {
    PaymentTransactionLifecycleEventListItem,
    PaymentTransactionLifecycleEventListResponse,
} from '@pillar-portal/api-types';
import {
    PaymentReconciliationStatus,
    PaymentTransactionLifecycleEventType,
    PaymentTransactionStatus,
} from '@pillar-portal/database/dist/frontend';
import { paymentsApi } from '@/lib/pillarApi';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import type {
    InfiniteScrollListFieldPredicateDescriptor,
    InfiniteScrollListJsonPathPredicateDescriptor,
} from '@/components/ui/infinite-scroll-list.types';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';

type LifecycleEventsQueryKey = readonly ['payments', 'transactionLifecycleEvents'];

const LifecycleFieldPredicateDescriptors: readonly InfiniteScrollListFieldPredicateDescriptor[] =
    [
        {
            field: 'event_type',
            label: 'Event type',
            operator: 'in',
            valueType: 'string',
            options: Object.values(PaymentTransactionLifecycleEventType).map(
                (eventType) => ({
                    value: eventType,
                    label: eventType,
                })
            ),
        },
        {
            field: 'transaction_status',
            label: 'Transaction status',
            operator: 'in',
            valueType: 'string',
            options: Object.values(PaymentTransactionStatus).map((status) => ({
                value: status,
                label: status,
            })),
        },
        {
            field: 'reconciled',
            label: 'Reconciliation',
            operator: 'in',
            valueType: 'string',
            options: Object.values(PaymentReconciliationStatus).map((status) => ({
                value: status,
                label: status,
            })),
        },
    ];

const LifecycleJsonPathPredicateDescriptors: readonly InfiniteScrollListJsonPathPredicateDescriptor[] =
    [
        { field: 'gateway_request', label: 'Gateway request' },
        { field: 'gateway_response_header', label: 'Gateway response header' },
        { field: 'gateway_response_json', label: 'Gateway response JSON' },
        { field: 'pillar_request', label: 'Portal request' },
        { field: 'pillar_response', label: 'Portal response' },
    ];

function formatOptionalDateTime(value: string): string {
    return new Date(value).toLocaleString();
}

function formatNullable(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
        return '-';
    }
    return String(value);
}

export function PaymentTransactionLifecycleEventsContent() {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const columns: Array<Column<PaymentTransactionLifecycleEventListItem>> =
        useMemo(
            () => [
                {
                    key: 'event_type',
                    header: 'Event',
                    render: (item) => item.event_type,
                },
                {
                    key: 'merchant_transaction_id',
                    header: 'Transaction',
                    render: (item) => item.merchant_transaction_id,
                },
                {
                    key: 'transaction_status',
                    header: 'Status',
                    render: (item) =>
                        item.transaction_status !== null &&
                        item.transaction_status !== undefined ? (
                            <Badge>{item.transaction_status}</Badge>
                        ) : (
                            '-'
                        ),
                },
                {
                    key: 'reconciled',
                    header: 'Reconciliation',
                    render: (item) => formatNullable(item.reconciled),
                },
                {
                    key: 'gateway_response_http_status',
                    header: 'HTTP',
                    render: (item) => formatNullable(item.gateway_response_http_status),
                },
                {
                    key: 'gateway_stan',
                    header: 'STAN',
                    render: (item) => formatNullable(item.gateway_stan),
                },
                {
                    key: 'gateway_rrn',
                    header: 'RRN',
                    render: (item) => formatNullable(item.gateway_rrn),
                },
                {
                    key: 'event_start',
                    header: 'Started',
                    render: (item) => formatOptionalDateTime(item.event_start),
                },
                {
                    key: 'error_message',
                    header: 'Error',
                    render: (item) => formatNullable(item.error_message),
                },
            ],
            []
        );

    return (
        <InfiniteScrollList<
            PaymentTransactionLifecycleEventListItem,
            string,
            LifecycleEventsQueryKey
        >
            pageTitle="Payment Transaction Lifecycle Events"
            cardTitle="Payment Transaction Lifecycle Events"
            queryPlaceholder="Omnisearch lifecycle events..."
            queryKeyPrefix={['payments', 'transactionLifecycleEvents']}
            pageSize={50}
            controlsPlacement="above-search"
            predicateControls={{
                fieldPredicates: LifecycleFieldPredicateDescriptors,
                jsonPathPredicates: LifecycleJsonPathPredicateDescriptors,
            }}
            fetchPage={async (params) => {
                const response: PaymentTransactionLifecycleEventListResponse =
                    await paymentsApi.listPaymentTransactionLifecycleEvents({
                        limit: params.limit,
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        ...(params.fieldPredicates !== undefined
                            ? { field_predicates: [...params.fieldPredicates] }
                            : {}),
                        ...(params.jsonPathPredicates !== undefined
                            ? { json_path_predicates: [...params.jsonPathPredicates] }
                            : {}),
                        ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
                    });
                return {
                    items: response.lifecycle_events,
                    pagination: response.pagination,
                };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected string cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading lifecycle events...',
                endOfListLabel: 'End of lifecycle events.',
                emptyLabel: 'No lifecycle events found.',
                genericErrorLabel: 'Failed to load lifecycle events.',
            }}
            renderItems={({ items }) => (
                <DataTable
                    data={[...items]}
                    columns={columns}
                    keyExtractor={(item) => item.payment_transaction_lifecycle_event_uuid}
                    paginationMode="none"
                    onRowClick={(item) => {
                        breadcrumbNavigate({
                            source: 'panel',
                            target: {
                                url: `/payment-transactions/${item.payment_transaction_uuid}`,
                                title: 'Transaction Details',
                            },
                        });
                    }}
                />
            )}
        />
    );
}
